import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AgGridReact } from "@ag-grid-community/react";
import "@ag-grid-community/styles/ag-grid.css";
import "@ag-grid-community/styles/ag-theme-quartz.css";
import { ClientSideRowModelModule } from "@ag-grid-community/client-side-row-model";
import { ModuleRegistry } from "@ag-grid-community/core";
import exportsvg from "../../assets/images/admin/dashboard/export.svg";
import { apiUrl } from "../../config/Config";

ModuleRegistry.registerModules([ClientSideRowModelModule]);

const Orders = () => {
  const navigate = useNavigate();
  const userdata = useSelector((store) => store.userReducer.userdata);
  const [orderStatus, setOrderStatus] = useState("allorders");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const paginationPageSizeSelector = useMemo(() => [10, 20, 50, 100], []);

  const headers = {
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + userdata?.token,
    },
  };

  const formatCurrency = (amount) => `₹ ${Number(amount || 0).toFixed(2)}`;

  const formatDate = (dateValue) => {
    if (!dateValue) return "NA";
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return dateValue;
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const statusClass = (status) => {
    if (["paid", "completed", "delivered"].includes(status)) return "completed";
    if (["failed", "cancelled", "canceled", "payment_failed"].includes(status)) return "cancelled";
    return "processing";
  };

  const fetchOrders = async () => {
    setLoading(true);
    await axios
      .post(apiUrl + "getadminorders", {}, headers)
      .then((res) => {
        if (res.data.status === "success") {
          setOrders(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          Swal.fire({
            text: res.data.message,
            icon: "error",
            confirmButtonColor: "rgb(59, 151, 196)",
          });
        }
      })
      .catch((err) => {
        Swal.fire({
          text: err.message,
          icon: "error",
          confirmButtonColor: "rgb(59, 151, 196)",
        });
      });
    setLoading(false);
  };

  useEffect(() => {
    if (userdata && Object.keys(userdata).length > 0) {
      fetchOrders();
    } else {
      navigate("/admin/login");
    }
  }, []);

  const rowData = useMemo(() => {
    const mappedOrders = orders.map((order) => ({
      id: order.id,
      orderID: order.order_number || `Order #${order.id}`,
      customerName: order.customer_name || "Customer",
      customerId: order.customer_id,
      customerNumber: order.contact_number || "NA",
      date: formatDate(order.created_at),
      status: order.payment_status || order.status || "pending",
      totalPrice: formatCurrency(order.amount),
      items: order.items_count || 0,
    }));

    if (orderStatus === "allorders") return mappedOrders;
    if (orderStatus === "completed") {
      return mappedOrders.filter((order) => ["paid", "completed", "delivered"].includes(order.status));
    }
    if (orderStatus === "cancelled") {
      return mappedOrders.filter((order) => ["failed", "cancelled", "canceled", "payment_failed"].includes(order.status));
    }
    return mappedOrders.filter((order) => statusClass(order.status) === "processing");
  }, [orders, orderStatus]);

  const colDefs = useMemo(
    () => [
      { field: "orderID", headerName: "Order ID", filter: true, flex: 1.2 },
      {
        field: "customerName",
        headerName: "Customer",
        filter: true,
        cellRenderer: (props) => (
          <button
            className="admin-link-cell"
            onClick={() => props.data.customerId && navigate(`/admin/customer-details/${props.data.customerId}`)}
          >
            {props.data.customerName}
          </button>
        ),
      },
      { field: "customerNumber", headerName: "Phone", filter: true },
      { field: "date", headerName: "Date" },
      { field: "items", headerName: "Items", width: 110 },
      { field: "totalPrice", headerName: "Total" },
      {
        field: "status",
        headerName: "Status",
        cellRenderer: (props) => (
          <div className={`orderstatusbg ${statusClass(props.data.status)}`}>
            <p>{props.data.status}</p>
          </div>
        ),
      },
    ],
    [navigate]
  );

  return (
    <div>
      <div className="dashboardbox" style={{ width: "100%" }}>
        <div className="productstogglebar mt-2">
          <div className="orderstatustogglediv">
            {[
              ["allorders", "All Orders"],
              ["processing", "Processing"],
              ["completed", "Completed"],
              ["cancelled", "Cancelled"],
            ].map(([key, label]) => (
              <div
                key={key}
                className={orderStatus === key ? "orderstatusdiv active" : "orderstatusdiv"}
                onClick={() => setOrderStatus(key)}
              >
                <p>{label}</p>
              </div>
            ))}
          </div>
          <div className="filterdiv" style={{ marginTop: -5 }}>
            <img src={exportsvg} className="orderexporticon" alt="" />
            <p>{loading ? "Loading..." : `${rowData.length} Orders`}</p>
          </div>
        </div>

        <div className="ag-theme-quartz mt-4" style={{ width: "100%", height: 550 }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={colDefs}
            defaultColDef={{ flex: 1 }}
            suppressRowClickSelection={true}
            groupSelectsChildren={true}
            rowSelection={"multiple"}
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={paginationPageSizeSelector}
            rowHeight={50}
          />
        </div>
      </div>
    </div>
  );
};

export default Orders;
